import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, timer } from 'rxjs';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PaymentService } from '../payment.service';
import { CryptoPayout, PayPaymentRequestResponse, PaymentRequest, PaymentRequestContract, PaymentRequestDetail as RequestDetail } from '../payment.type';
import { SupplierInfoDialog } from '../../supplier/supplier-info-dialog/supplier-info-dialog';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { RejectRequestDialog } from '../reject-request-dialog/reject-request-dialog';
import { PayRequestDialog } from '../pay-request-dialog/pay-request-dialog';
import { PaymentState } from '../payment.state';
import { paymentLabel, statusClass } from '../payment-format';
import { Viewport } from '../../shared/viewport';
import { AuthService } from '../../auth/auth.service';

/**
 * One payment request and exactly what it covers: the transfer runs and manual
 * contracts that made up its amount when the supplier asked. Paying it is one
 * step (PayRequestDialog) and records exactly those rows, already marked paid —
 * work that finished after the request waits for the supplier's next one.
 *
 * A supplier may have several pending requests (one per cooldown); each covers
 * different rows, so the others are listed here to show they are not duplicates.
 *
 * A USDT request on an on-chain method can instead be sent from the hot wallet:
 * the server signs, sends and waits for it to be mined, then records the same
 * settlement. The page refreshes itself while that payout is in flight.
 */
@Component({
  selector: 'app-payment-request-detail',
  imports: [
    CommonModule, FormsModule, RouterLink, MatTableModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatProgressSpinnerModule, MatTooltipModule,
  ],
  templateUrl: './payment-request-detail.html',
  styleUrl: './payment-request-detail.scss',
})
export class PaymentRequestDetail implements OnInit {
  readonly state = inject(PaymentState);
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  private route = inject(ActivatedRoute);
  private paymentService = inject(PaymentService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);

  readonly statusClass = statusClass;
  readonly paymentLabel = paymentLabel;
  readonly isMobile$ = inject(Viewport).isMobile$;
  readonly runColumns = ['date', 'account', 'coins', 'fee', 'requested', 'now', 'payment'];
  readonly contractColumns = ['date', 'name', 'coins', 'fee', 'requested', 'now', 'payment'];
  readonly maxNoteLength = 500;

  request: RequestDetail | null = null;
  /** The supplier's other pending requests, oldest first. */
  otherPending: PaymentRequest[] = [];
  loading = false;
  error: string | null = null;

  adminNote = '';
  savingNote = false;
  cancelling = false;
  rejecting = false;
  updatingAmount = false;
  sendingOnChain = false;
  cancellingPayout = false;

  private id = '';
  private poll?: Subscription;

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(p => { this.id = p.get('id') ?? ''; this.load(); });
    this.state.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => this.poll?.unsubscribe());
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getPaymentRequest(this.id).subscribe({
      next: request => {
        this.request = request;
        this.adminNote = request.admin_note ?? '';
        this.loading = false;
        this.loadOtherPending(request);
        this.pollWhileInFlight();
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load the payment request'; this.loading = false; },
    });
  }

  private loadOtherPending(request: RequestDetail): void {
    this.otherPending = [];
    if (request.status !== 'Pending') return;
    this.paymentService.getPaymentRequests({ supplierId: request.supplier_id, status: 'Pending', pageSize: 100 })
      .subscribe({
        next: res => {
          if (this.request?.id !== request.id) return;
          this.otherPending = res.requests
            .filter(r => r.id !== request.id)
            .sort((a, b) => a.created_at.localeCompare(b.created_at));
        },
        // Only a hint: the page works without it.
        error: () => { this.otherPending = []; },
      });
  }

  get isPending(): boolean { return this.request?.status === 'Pending'; }

  get payout(): CryptoPayout | undefined { return this.request?.crypto_payout; }

  /** Queued or waiting to be mined: nothing else may touch the request meanwhile. */
  get payoutInFlight(): boolean {
    const s = this.payout?.status;
    return s === 'Queued' || s === 'Signed';
  }

  /** A pending, payable USDT request on an on-chain method, never sent before. */
  get canSendOnChain(): boolean {
    const r = this.request;
    return !!r?.payout_network && this.isPending && r.payable && !this.payoutInFlight && this.payout?.status !== 'Confirmed';
  }

  /** Sent on chain, but the settlement is not recorded: the admin records it by hand. */
  get payoutUnrecorded(): boolean {
    return this.payout?.status === 'Confirmed' && !this.payout.settlement_id;
  }

  payoutLabel(p: CryptoPayout): string {
    switch (p.status) {
      case 'Queued': return 'Queued';
      case 'Signed': return 'Sent, waiting to be mined';
      case 'Confirmed': return p.settlement_id ? 'Sent and recorded' : 'Sent, not recorded';
      default: return 'Not sent';
    }
  }

  private pollWhileInFlight(): void {
    this.poll?.unsubscribe();
    if (!this.payoutInFlight) return;
    this.poll = timer(4000).subscribe(() => this.load());
  }

  get runsRequested(): number { return this.request?.runs.reduce((s, r) => s + r.requested_amount, 0) ?? 0; }
  get contractsRequested(): number { return this.request?.contracts.reduce((s, c) => s + c.requested_amount, 0) ?? 0; }

  contractLabel(c: PaymentRequestContract): string {
    return c.name || c.id.slice(0, 8);
  }

  // ── Actions ──

  pay(): void {
    if (!this.request?.payable) return;
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(PayRequestDialog, {
      data: { request: this.request },
      width: isMobile ? '100vw' : '520px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '92vh',
      autoFocus: false,
    }).afterClosed().subscribe((res: PayPaymentRequestResponse | null) => {
      if (!res) return;
      this.snackBar.open(`Paid ${res.settlement.amount.toLocaleString()} Toman to ${this.request?.supplier_name}`, 'Close', { duration: 5000 });
      this.load();
    });
  }

  sendOnChain(): void {
    const r = this.request;
    if (!r || !this.canSendOnChain) return;
    this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Send USDT from the hot wallet',
        message: `Send ${r.payout_amount} ${r.payout_currency ?? 'USDT'} (${r.payout_network}) to ${r.payout_account} for `
          + `${r.supplier_name}? The server sends it, waits for it to be mined, then records the payment of `
          + `${r.amount.toLocaleString()} Toman. A sent transfer cannot be taken back.`,
      },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.sendingOnChain = true;
      this.paymentService.sendCryptoPayout(r.id, r.payout_amount!).subscribe({
        next: () => {
          this.sendingOnChain = false;
          this.snackBar.open('Queued — this page updates as it is sent and mined.', 'Close', { duration: 5000 });
          this.load();
        },
        error: err => {
          this.sendingOnChain = false;
          this.snackBar.open(err?.error?.error ?? 'Failed to send', 'Close', { duration: 8000 });
          this.load();
        },
      });
    });
  }

  cancelPayout(): void {
    const p = this.payout;
    if (p?.status !== 'Queued') return;
    this.cancellingPayout = true;
    this.paymentService.cancelCryptoPayout(p.id).subscribe({
      next: () => {
        this.cancellingPayout = false;
        this.snackBar.open('On-chain payout cancelled — nothing was sent.', 'Close', { duration: 5000 });
        this.load();
      },
      error: err => {
        this.cancellingPayout = false;
        this.snackBar.open(err?.error?.error ?? 'Failed to cancel', 'Close', { duration: 6000 });
        this.load();
      },
    });
  }

  /**
   * For a request blocked only because what it covers changed value since it
   * was sent (a late-detected sale on a finished contract, say): moves it to
   * what it is worth now, after which it can be paid as usual.
   */
  updateAmount(): void {
    const r = this.request;
    if (!r?.amount_updatable) return;
    const diff = r.current_amount - r.amount;
    let message = `Update ${r.supplier_name}'s request from ${r.amount.toLocaleString()} to `
      + `${r.current_amount.toLocaleString()} Toman (${diff > 0 ? '+' : ''}${diff.toLocaleString()}), `
      + `what the runs and contracts it covers are worth now. You then pay the new amount.`;
    if (r.payout_rate && r.payout_currency) {
      message += ` The ${r.payout_currency} amount is recomputed at the rate the supplier accepted `
        + `(${(+r.payout_rate).toLocaleString()} Toman), not today's.`;
    }
    if (r.original_amount == null) {
      message += ` The ${r.amount.toLocaleString()} the supplier asked for stays on the request.`;
    }
    this.dialog.open(ConfirmDialog, {
      data: { title: 'Update the request amount', message },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.updatingAmount = true;
      this.paymentService.updatePaymentRequestAmount(r.id, r.current_amount).subscribe({
        next: detail => {
          this.request = detail;
          this.adminNote = detail.admin_note ?? '';
          this.updatingAmount = false;
          this.snackBar.open(`Request updated to ${detail.amount.toLocaleString()} Toman — you can pay it now.`, 'Close', { duration: 5000 });
        },
        error: err => {
          this.updatingAmount = false;
          this.snackBar.open(err?.error?.error ?? 'Failed to update the amount', 'Close', { duration: 6000 });
          this.load();
        },
      });
    });
  }

  openSupplierInfoDialog(): void {
    if (!this.request) return;
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: this.request.supplier_id, supplierUsername: this.request.supplier_name },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  saveNote(): void {
    if (!this.request) return;
    this.savingNote = true;
    this.paymentService.updatePaymentRequestNote(this.request.id, this.adminNote).subscribe({
      next: r => {
        this.request = { ...this.request!, admin_note: r.admin_note };
        this.adminNote = r.admin_note ?? '';
        this.savingNote = false;
        this.snackBar.open('Note saved — the supplier can read it.', 'Close', { duration: 4000 });
      },
      error: err => {
        this.savingNote = false;
        this.snackBar.open(err?.error?.error ?? 'Failed to save the note', 'Close', { duration: 6000 });
      },
    });
  }

  rejectRequest(): void {
    if (!this.request) return;
    const id = this.request.id;
    this.dialog.open(RejectRequestDialog, {
      width: '460px',
      maxWidth: '95vw',
      data: { supplierName: this.request.supplier_name },
    }).afterClosed().subscribe((reason: string | null) => {
      if (!reason) return;
      this.rejecting = true;
      this.paymentService.rejectPaymentRequest(id, reason).subscribe({
        next: () => {
          this.rejecting = false;
          this.snackBar.open('Request rejected — the supplier can read the reason.', 'Close', { duration: 5000 });
          this.load();
        },
        error: err => {
          this.rejecting = false;
          this.snackBar.open(err?.error?.error ?? 'Failed to reject', 'Close', { duration: 6000 });
        },
      });
    });
  }

  cancelRequest(): void {
    if (!this.request) return;
    const id = this.request.id;
    this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Cancel payment request',
        message: `Cancel ${this.request.supplier_name}'s request? They still cannot send another one until their cooldown from when it was made has passed.`,
        confirmText: 'Cancel request',
      },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.cancelling = true;
      this.paymentService.cancelPaymentRequest(id).subscribe({
        next: () => {
          this.cancelling = false;
          this.snackBar.open('Payment request cancelled', 'Close', { duration: 4000 });
          this.load();
        },
        error: err => {
          this.cancelling = false;
          this.snackBar.open(err?.error?.error ?? 'Failed to cancel', 'Close', { duration: 6000 });
        },
      });
    });
  }
}
